package com.appsyde.contract.security

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject


data class SemAnonymizationResult(
    val anonymizedText: String,
    val replacements: Map<String, String>
)


public fun deanonymizeJson(
    jsonStr: String,
    replacements: Map<String, String>
): String {
    val el = Json.parseToJsonElement(jsonStr)
    val dean = deanonymizeJsonElement(el, replacements)
    // Json.encodeToString(JsonElement.serializer(), dean) marche aussi
    return dean.toString() // toString() re-sérialise en JSON valide + échappé
}

public fun deanonymizeJsonElement(
    el: JsonElement,
    replacements: Map<String, String>
): JsonElement {
    return when (el) {
        is JsonObject -> buildJsonObject {
            for ((k, v) in el) {
                put(k, deanonymizeJsonElement(v, replacements))
            }
        }
        is JsonArray -> buildJsonArray {
            for (v in el) add(deanonymizeJsonElement(v, replacements))
        }
        is JsonPrimitive -> {
            if (el.isString) {
                val raw = el.content
                val replaced = SemanticAnonymizer.deanonymize(raw, replacements)
                JsonPrimitive(replaced) // ✅ ré-échappe automatiquement à la sérialisation
            } else el
        }
        else -> el
    }
}


object SemanticAnonymizer {

    /** Types used by [token]; must match [token] format for detection. */
    private val ANON_TYPES = setOf("EMAIL", "PHONE", "URL", "ORDER", "NAME", "ADDR", "HINT")

    /**
     * True if [s] looks like an anonymizer token (e.g. NAME_001 or [EMAIL_002]).
     * Use to avoid persisting such values as memorized keys / missing / toConfirm (security).
     */
    fun looksLikeAnonymizerToken(s: String?): Boolean {
        if (s.isNullOrBlank()) return false
        val t = s.trim()
        // With brackets: [TYPE_NNN] or without: TYPE_NNN (AI may return without brackets)
        val withoutBrackets = t.removeSurrounding("[", "]")
        val parts = withoutBrackets.split("_")
        if (parts.size != 2) return false
        if (parts[1].length !in 1..3 || !parts[1].all { it.isDigit() }) return false
        return parts[0].uppercase() in ANON_TYPES
    }

    private val emailRegex = Regex("""[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}""")
    private val phoneRegex = Regex("""(\+?\d[\d .-]{7,}\d)""")
    private val urlRegex   = Regex("""https?://\S+""")
    /** #123, #91542 */
    private val orderRegex = Regex("""#\d{3,}""")
    /** "commande 91542", "réf. 12345", "n° 67890" → token, stored value = digits only */
    private val orderRegex2 = Regex("""(?:commande|réf\.?|ref\.?|n°\s*|numéro\s+)\s*(\d{3,})""", RegexOption.IGNORE_CASE)

    private val fullNameRegex =
        Regex("""\b[A-Z][a-zéèêëàïîôöùüç'-]{2,}\s+[A-Z][a-zéèêëàïîôöùüç'-]{2,}\b""")

    private val addressRegex = Regex(
        """\b\d{1,4}\s*(?:bis|ter)?\s*(?:rue|avenue|av\.?|boulevard|bd\.?|chemin|impasse|allée|allee|route|rt)\s+[^.,\n]+""",
        RegexOption.IGNORE_CASE
    )

    private fun token(type: String, i: Int) = "[${type}_${i.toString().padStart(3, '0')}]"

    fun anonymize(text: String, nameHints: List<String> = emptyList()): SemAnonymizationResult {
        var working = text
        val map = linkedMapOf<String, String>()

        // per-type counters (stable + no collisions within type)
        var emailI = 1
        var phoneI = 1
        var urlI = 1
        var orderI = 1
        var nameI = 1
        var addrI = 1
        var hintI = 1

        fun replace(regex: Regex, type: String, captureGroup: Int? = null, nextIndex: () -> Int) {
            working = regex.replace(working) { m ->
                val original = captureGroup?.let { g -> m.groupValues.getOrNull(g)?.takeIf { it.isNotBlank() } } ?: m.value

                // reuse existing token if same original already replaced
                val existing = map.entries.firstOrNull { it.value == original }?.key
                if (existing != null) return@replace existing

                val t = token(type, nextIndex())
                map[t] = original
                t
            }
        }

        replace(emailRegex, "EMAIL") { emailI++ }

        // Phone: unique token per phone (keeps multiplicity)
        replace(phoneRegex, "PHONE") { phoneI++ }

        replace(urlRegex, "URL") { urlI++ }
        replace(orderRegex, "ORDER") { orderI++ }
        replace(orderRegex2, "ORDER", captureGroup = 1) { orderI++ }
        replace(fullNameRegex, "NAME") { nameI++ }
        replace(addressRegex, "ADDR") { addrI++ }

        // hints: word-boundary replace (avoid inside words), best-effort
        nameHints.distinct().forEach { name ->
            val n = name.trim()
            if (n.isBlank()) return@forEach
            val r = Regex("""\b${Regex.escape(n)}\b""")
            if (!r.containsMatchIn(working)) return@forEach

            val t = token("HINT", hintI++)
            map[t] = n
            working = r.replace(working, t)
        }

        // LLM / JSON often return tokens WITHOUT brackets (e.g. ORDER_001). Ensure deanonymize
        // can replace both forms so we never persist or display placeholders.
        val extendedMap = LinkedHashMap<String, String>(map)
        map.forEach { (tokenStr, original) ->
            if (tokenStr.startsWith("[") && tokenStr.endsWith("]")) {
                val withoutBrackets = tokenStr.removeSurrounding("[", "]")
                extendedMap[withoutBrackets] = original
            }
        }

        return SemAnonymizationResult(anonymizedText = working, replacements = extendedMap)
    }

    fun deanonymize(text: String, replacements: Map<String, String>): String {
        var result = text

        // Replace longer tokens first to avoid partial overlaps
        val entries = replacements.entries.sortedByDescending { it.key.length }

        for ((fake, original) in entries) {
            result = result.replace(fake, original)
        }
        return result
    }
}
